import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams } from "wouter";
import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/lib/picker-search";
import {
  Sparkles, Clock, CheckCircle2,
  XCircle, Loader2, AlertTriangle, Users, Package, Tag, Target,
  ChevronRight, Heart, Trash2, Search, ArrowUpDown,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import type { Persona, Product, Offer, NarrativeGeneration, NarrativeAngle } from "@shared/schema";
import { selectablePersonas } from "@shared/persona-select";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { NarrativeFunnelStageBadge } from "@/components/narrative-funnel-stage-badge";

const STATUS_CONFIG: Record<string, { label: string; icon: any; className: string }> = {
  queued: { label: "Queued", icon: Clock, className: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400" },
  running: { label: "Running", icon: Loader2, className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  generating_angles: { label: "Generating angles", icon: Loader2, className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  completed: { label: "Completed", icon: CheckCircle2, className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" },
  failed: { label: "Failed", icon: XCircle, className: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
  canceled: { label: "Canceled", icon: AlertTriangle, className: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
};

export const FAMILY_COLORS: Record<string, string> = {
  Conflict: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  Identity: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
  Outcome: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  Trust: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  "Life-Fit": "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  Worldview: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
};

export function GenerationStatusBadge({ status }: { status: string }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.queued;
  const Icon = config.icon;
  return (
    <Badge className={`text-[10px] rounded-full px-2.5 gap-1 ${config.className}`}>
      <Icon className={`h-3 w-3 ${status === "running" || status === "generating_angles" ? "animate-spin" : ""}`} />
      {config.label}
    </Badge>
  );
}

export function NarrativeAngleCard({ angle, brandId }: { angle: NarrativeAngle; brandId: string }) {
  const { toast } = useToast();
  const favMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/narrative-angles/${angle.id}/favorite`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/narrative-angles`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/narrative-generations`] });
      if (angle.generationId) {
        queryClient.invalidateQueries({ queryKey: [`/api/narrative-generations/${angle.generationId}`] });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/narrative-angles/${angle.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/narrative-angles`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/narrative-generations`] });
      if (angle.generationId) {
        queryClient.invalidateQueries({ queryKey: [`/api/narrative-generations/${angle.generationId}`] });
      }
      toast({ title: "Angle deleted" });
    },
  });

  const proofAnchors = (angle.proofAnchorsJson as string[] | null) || [];
  const scores = [angle.resonanceScore, angle.proofScore, angle.distinctivenessScore, angle.emotionalScore].filter((s): s is number => s !== null);
  const avgScore = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;

  const scoreItems = [
    { label: "Resonance", short: "R", value: angle.resonanceScore, color: "bg-violet-500" },
    { label: "Proof", short: "P", value: angle.proofScore, color: "bg-emerald-500" },
    { label: "Distinct", short: "D", value: angle.distinctivenessScore, color: "bg-amber-500" },
    { label: "Emotional", short: "E", value: angle.emotionalScore, color: "bg-rose-500" },
  ].filter((s): s is { label: string; short: string; value: number; color: string } => s.value !== null);

  return (
    <Link href={`/brands/${brandId}/narrative-angles/${angle.id}`}>
      <Card
        className="group cursor-pointer border hover:shadow-md hover:border-primary/20 transition-all duration-200"
        data-testid={`card-angle-${angle.id}`}
      >
        <CardContent className="p-0">
          <div className="p-4 pb-3">
            <div className="flex items-start justify-between gap-2 mb-2.5">
              <div className="flex items-center gap-1.5">
                {angle.family && (
                  <Badge className={`text-[9px] rounded-full px-2 py-0 ${FAMILY_COLORS[angle.family] || "bg-gray-100 text-gray-700"}`}>
                    {angle.family}
                  </Badge>
                )}
                <NarrativeFunnelStageBadge stage={angle.funnelStage} />
              </div>
              <div className="shrink-0 flex items-center gap-0.5">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    favMutation.mutate();
                  }}
                  className="p-1 hover:bg-muted/50 rounded-full transition-colors"
                  data-testid={`button-fav-${angle.id}`}
                >
                  <Heart className={`h-3.5 w-3.5 ${angle.isFavorite ? "fill-red-500 text-red-500" : "text-muted-foreground/30 group-hover:text-muted-foreground/50"}`} />
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    deleteMutation.mutate();
                  }}
                  className="p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors"
                  data-testid={`button-delete-angle-${angle.id}`}
                  title="Delete angle"
                >
                  <Trash2 className="h-3.5 w-3.5 text-muted-foreground/30 group-hover:text-muted-foreground/50 hover:text-red-500" />
                </button>
              </div>
            </div>
            <h3 className="text-[14px] font-semibold leading-snug tracking-tight mb-1.5" data-testid={`text-angle-name-${angle.id}`}>{angle.name}</h3>
            {angle.summary && (
              <p className="text-[12px] text-muted-foreground/60 leading-relaxed line-clamp-2">{angle.summary}</p>
            )}
          </div>

          {scoreItems.length > 0 && (
            <div className="bg-muted/30 dark:bg-muted/15 border-t border-border/40 px-4 py-3 rounded-b-xl">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
                  {scoreItems.map(({ label, short, value, color }) => (
                    <div key={short} className="flex items-center gap-1" title={`${label}: ${value}/10`}>
                      <div className={`h-1.5 w-1.5 rounded-full ${color}`} />
                      <span className="text-[10px] font-semibold text-muted-foreground/60">{short}</span>
                      <span className="text-[11px] font-bold tabular-nums text-foreground/70">{value}</span>
                    </div>
                  ))}
                </div>
                {avgScore > 0 && (
                  <div className="flex items-center gap-1 pl-2 border-l border-border/40 shrink-0">
                    <span className="text-[9px] font-medium text-muted-foreground/40 uppercase">Avg</span>
                    <span className="text-[13px] font-bold tabular-nums text-primary">{avgScore.toFixed(1)}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

export default function NarrativeEnginePage() {
  const { brandId } = useParams<{ brandId: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [selectedPersonaId, setSelectedPersonaId] = useState<string>("");
  const [promotedItemType, setPromotedItemType] = useState<"product" | "offering">("product");
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  const [selectedOfferingId, setSelectedOfferingId] = useState<string>("");

  const { data: personas = [] } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const { data: products = [] } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const { data: offerings = [] } = useQuery<Offer[]>({
    queryKey: [`/api/brands/${brandId}/offers`],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const { data: generations = [], isLoading: gensLoading } = useQuery<NarrativeGeneration[]>({
    queryKey: [`/api/brands/${brandId}/narrative-generations`],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const { data: favoriteAngles = [] } = useQuery<NarrativeAngle[]>({
    queryKey: [`/api/brands/${brandId}/narrative-angles`, "favorites"],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/narrative-angles?favorite=true`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load");
      return res.json();
    },
  });

  const generateMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        personaId: selectedPersonaId,
        promotedItemType,
      };
      if (promotedItemType === "product") {
        payload.productId = selectedProductId;
      } else {
        payload.offeringId = selectedOfferingId;
      }
      const res = await apiRequest("POST", `/api/brands/${brandId}/narrative-generations`, payload);
      return res.json();
    },
    onSuccess: (gen: NarrativeGeneration) => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/narrative-generations`] });
      toast({ title: "Generation started" });
      navigate(`/brands/${brandId}/narrative-generations/${gen.id}`);
    },
    onError: (e: Error) => {
      toast({ title: "Generation failed", description: e.message, variant: "destructive" });
    },
  });

  const deleteGenMutation = useMutation({
    mutationFn: async (genId: string) => {
      await apiRequest("DELETE", `/api/narrative-generations/${genId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/narrative-generations`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/narrative-angles`] });
      toast({ title: "Generation deleted" });
    },
    onError: (e: Error) => {
      toast({ title: "Delete failed", description: e.message, variant: "destructive" });
    },
  });

  const activePersonas = selectablePersonas(personas);
  const activeProducts = products.filter(p => p.status === "active");
  const activeOfferings = offerings.filter(o => o.status !== "archived");

  const personaMap = new Map(personas.map(p => [p.id, p]));
  const productMap = new Map(products.map(p => [p.id, p]));
  const offeringMap = new Map(offerings.map(o => [o.id, o]));

  const itemSelected = promotedItemType === "product" ? !!selectedProductId : !!selectedOfferingId;

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto space-y-8">
      <div className="fade-in-up">
        <div className="flex items-center gap-3 mb-1">
          <Sparkles className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold tracking-tight">Narrative Engine</h1>
        </div>
        <p className="text-sm text-muted-foreground ml-9">
          Find strong messaging directions at the intersection of persona psychology and what you're promoting.
        </p>
      </div>

      <Card className="fade-in-up border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
        <CardContent className="p-6 space-y-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider">Run Narrative Analysis</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" />
                Persona
              </label>
              <SearchableSelect
                value={selectedPersonaId}
                onValueChange={setSelectedPersonaId}
                placeholder="Select a persona"
                testId="select-persona"
                triggerClassName="rounded-lg"
                options={activePersonas.map(p => ({ value: p.id, label: p.name }))}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Target className="h-3.5 w-3.5" />
                Promote
              </label>
              <Select
                value={promotedItemType}
                onValueChange={(v) => {
                  const next = v as "product" | "offering";
                  setPromotedItemType(next);
                  setSelectedProductId("");
                  setSelectedOfferingId("");
                }}
              >
                <SelectTrigger data-testid="select-promoted-type" className="rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="product">Product</SelectItem>
                  <SelectItem value="offering">Offering</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {promotedItemType === "product" ? (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <Package className="h-3.5 w-3.5" />
                  Product
                </label>
                <SearchableSelect
                  value={selectedProductId}
                  onValueChange={setSelectedProductId}
                  placeholder="Select a product"
                  testId="select-product"
                  triggerClassName="rounded-lg"
                  options={activeProducts.map(p => ({ value: p.id, label: p.name }))}
                />
              </div>
            ) : (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <Tag className="h-3.5 w-3.5" />
                  Offering
                </label>
                <SearchableSelect
                  value={selectedOfferingId}
                  onValueChange={setSelectedOfferingId}
                  placeholder="Select an offering"
                  testId="select-offering"
                  triggerClassName="rounded-lg"
                  options={
                    activeOfferings.length === 0
                      ? [{ value: "__none", label: "No offerings yet", disabled: true }]
                      : activeOfferings.map(o => ({ value: o.id, label: o.name }))
                  }
                />
              </div>
            )}
          </div>
          <Button
            className="rounded-full w-full md:w-auto"
            disabled={!selectedPersonaId || !itemSelected || generateMutation.isPending}
            onClick={() => generateMutation.mutate()}
            data-testid="button-generate"
          >
            {generateMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Analyze
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <Tabs defaultValue="favorites" className="fade-in-up-delay-1">
        <TabsList className="rounded-full">
          <TabsTrigger value="favorites" className="rounded-full text-xs gap-1.5" data-testid="tab-favorites">
            <Heart className="h-3.5 w-3.5" />
            Favorite Angles
            <Badge variant="secondary" className="text-[10px] rounded-full px-1.5 ml-1">{favoriteAngles.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="past" className="rounded-full text-xs gap-1.5" data-testid="tab-past-generations">
            <Clock className="h-3.5 w-3.5" />
            Past Generations
            <Badge variant="secondary" className="text-[10px] rounded-full px-1.5 ml-1">{generations.length}</Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="favorites" className="mt-4">
          {favoriteAngles.length === 0 ? (
            <div className="rounded-xl border border-dashed p-12 text-center">
              <Heart className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No favorite angles yet</p>
              <p className="text-xs text-muted-foreground mt-1">Open a generation and tap the heart on any angle you want to save.</p>
            </div>
          ) : (
            <FavoriteAnglesTable
              favoriteAngles={favoriteAngles}
              personaMap={personaMap}
              productMap={productMap}
              offeringMap={offeringMap}
              brandId={brandId}
            />
          )}
        </TabsContent>

        <TabsContent value="past" className="mt-4">
          {gensLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-20 w-full rounded-xl" />
              <Skeleton className="h-20 w-full rounded-xl" />
            </div>
          ) : generations.length === 0 ? (
            <div className="rounded-xl border border-dashed p-12 text-center">
              <Sparkles className="h-10 w-10 text-muted-foreground/20 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No generations yet</p>
              <p className="text-xs text-muted-foreground mt-1">Select a persona and product above to get started.</p>
            </div>
          ) : (
            <PastGenerationsTable
              generations={generations}
              personaMap={personaMap}
              productMap={productMap}
              offeringMap={offeringMap}
              brandId={brandId}
              onDelete={(id) => deleteGenMutation.mutate(id)}
              deletePending={deleteGenMutation.isPending}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function resolvePromotedName(
  promotedItemType: string | null | undefined,
  productId: string | null,
  offeringId: string | null,
  productMap: Map<string, Product>,
  offeringMap: Map<string, Offer>,
): string {
  if (promotedItemType === "offering" && offeringId) {
    return offeringMap.get(offeringId)?.name || "Unknown Offering";
  }
  if (productId) return productMap.get(productId)?.name || "Unknown Product";
  if (offeringId) return offeringMap.get(offeringId)?.name || "Unknown Offering";
  return "—";
}

function FavoriteAnglesTable({
  favoriteAngles,
  personaMap,
  productMap,
  offeringMap,
  brandId,
}: {
  favoriteAngles: NarrativeAngle[];
  personaMap: Map<string, Persona>;
  productMap: Map<string, Product>;
  offeringMap: Map<string, Offer>;
  brandId: string;
}) {
  const { toast } = useToast();
  const favMutation = useMutation({
    mutationFn: async (angleId: string) => {
      await apiRequest("POST", `/api/narrative-angles/${angleId}/favorite`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/narrative-angles`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/narrative-generations`] });
    },
  });
  const deleteMutation = useMutation({
    mutationFn: async (angleId: string) => {
      await apiRequest("DELETE", `/api/narrative-angles/${angleId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/narrative-angles`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/narrative-generations`] });
      toast({ title: "Angle deleted" });
    },
  });

  const getAvg = (a: NarrativeAngle) => {
    const s = [a.resonanceScore, a.proofScore, a.distinctivenessScore, a.emotionalScore].filter((x): x is number => x !== null);
    return s.length > 0 ? s.reduce((p, c) => p + c, 0) / s.length : 0;
  };

  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<"avg" | "family" | "persona" | "product" | "name">("avg");
  const [personaFilter, setPersonaFilter] = useState<string>("all");
  const [promotedFilter, setPromotedFilter] = useState<string>("all");

  const angleItemName = (a: NarrativeAngle) =>
    resolvePromotedName(null, a.productId, a.offeringId, productMap, offeringMap);

  const angleItemKey = (a: NarrativeAngle) =>
    a.productId ? `product:${a.productId}` : a.offeringId ? `offering:${a.offeringId}` : "none";

  const personaOptions = Array.from(
    new Map(
      favoriteAngles
        .map((a) => [a.personaId || "__deleted__", personaMap.get(a.personaId || "")?.name || "Persona deleted"] as const)
    ).entries()
  ).sort((a, b) => a[1].localeCompare(b[1]));

  const promotedOptions = Array.from(
    new Map(favoriteAngles.map((a) => [angleItemKey(a), angleItemName(a)] as const)).entries()
  ).sort((a, b) => a[1].localeCompare(b[1]));

  const q = search.trim().toLowerCase();
  const filtered = favoriteAngles.filter((a) => {
    if (personaFilter !== "all" && (a.personaId || "__deleted__") !== personaFilter) return false;
    if (promotedFilter !== "all" && angleItemKey(a) !== promotedFilter) return false;
    if (!q) return true;
    const personaName = (personaMap.get(a.personaId || "")?.name || "Persona deleted").toLowerCase();
    const itemName = angleItemName(a).toLowerCase();
    return (
      a.name.toLowerCase().includes(q) ||
      (a.summary?.toLowerCase().includes(q) ?? false) ||
      (a.family?.toLowerCase().includes(q) ?? false) ||
      personaName.includes(q) ||
      itemName.includes(q)
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    switch (sortBy) {
      case "name":
        return a.name.localeCompare(b.name);
      case "family":
        return (a.family || "").localeCompare(b.family || "") || getAvg(b) - getAvg(a);
      case "persona": {
        const pa = personaMap.get(a.personaId || "")?.name || "Persona deleted";
        const pb = personaMap.get(b.personaId || "")?.name || "Persona deleted";
        return pa.localeCompare(pb) || getAvg(b) - getAvg(a);
      }
      case "product": {
        return angleItemName(a).localeCompare(angleItemName(b)) || getAvg(b) - getAvg(a);
      }
      case "avg":
      default:
        return getAvg(b) - getAvg(a);
    }
  });

  return (
    <div>
      <TableToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search angle, family, persona, or product…"
        sortBy={sortBy}
        onSortChange={(v) => setSortBy(v as typeof sortBy)}
        sortOptions={[
          { value: "avg", label: "Avg score (high → low)" },
          { value: "name", label: "Angle name (A → Z)" },
          { value: "family", label: "Family" },
          { value: "persona", label: "Persona" },
          { value: "product", label: "Promoted" },
        ]}
        resultCount={sorted.length}
        totalCount={favoriteAngles.length}
        testIdPrefix="favorites"
      />
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <div className="flex-1">
          <Select value={personaFilter} onValueChange={setPersonaFilter}>
            <SelectTrigger className="h-9 rounded-full text-xs gap-1.5" data-testid="select-filter-persona">
              <Users className="h-3.5 w-3.5 text-muted-foreground/70" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">All personas ({favoriteAngles.length})</SelectItem>
              {personaOptions.map(([id, name]) => {
                const count = favoriteAngles.filter((a) => (a.personaId || "__deleted__") === id).length;
                return (
                  <SelectItem key={id} value={id} className="text-xs">{name} ({count})</SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1">
          <Select value={promotedFilter} onValueChange={setPromotedFilter}>
            <SelectTrigger className="h-9 rounded-full text-xs gap-1.5" data-testid="select-filter-promoted">
              <Package className="h-3.5 w-3.5 text-muted-foreground/70" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">All promoted ({favoriteAngles.length})</SelectItem>
              {promotedOptions.map(([key, name]) => {
                const count = favoriteAngles.filter((a) => angleItemKey(a) === key).length;
                return (
                  <SelectItem key={key} value={key} className="text-xs">{name} ({count})</SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        {(personaFilter !== "all" || promotedFilter !== "all") && (
          <Button
            variant="ghost"
            size="sm"
            className="h-9 rounded-full text-xs"
            onClick={() => { setPersonaFilter("all"); setPromotedFilter("all"); }}
            data-testid="button-clear-filters"
          >
            Clear filters
          </Button>
        )}
      </div>
      <div className="rounded-xl border border-border/60 overflow-hidden bg-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40 border-b border-border/60 text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="text-left font-semibold px-4 py-2.5">Family</th>
                <th className="text-left font-semibold px-4 py-2.5">Angle</th>
                <th className="text-left font-semibold px-4 py-2.5 hidden md:table-cell">Persona</th>
                <th className="text-left font-semibold px-4 py-2.5 hidden md:table-cell">Promoted</th>
                <th className="text-center font-semibold px-2 py-2.5 hidden lg:table-cell" title="Resonance">R</th>
                <th className="text-center font-semibold px-2 py-2.5 hidden lg:table-cell" title="Proof">P</th>
                <th className="text-center font-semibold px-2 py-2.5 hidden lg:table-cell" title="Distinctiveness">D</th>
                <th className="text-center font-semibold px-2 py-2.5 hidden lg:table-cell" title="Emotional">E</th>
                <th className="text-center font-semibold px-3 py-2.5">Avg</th>
                <th className="px-3 py-2.5 w-[1%]"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((angle) => {
                const persona = personaMap.get(angle.personaId || "");
                const itemName = angleItemName(angle);
                const avg = getAvg(angle);
                return (
                  <tr
                    key={angle.id}
                    className="border-b border-border/40 last:border-b-0 hover:bg-muted/20 transition-colors group"
                    data-testid={`row-favorite-angle-${angle.id}`}
                  >
                    <td className="px-4 py-3 align-top">
                      {angle.family && (
                        <Badge className={`text-[9px] rounded-full px-2 py-0 ${FAMILY_COLORS[angle.family] || "bg-gray-100 text-gray-700"}`}>
                          {angle.family}
                        </Badge>
                      )}
                      <NarrativeFunnelStageBadge stage={angle.funnelStage} />
                    </td>
                    <td className="px-4 py-3 align-top max-w-md">
                      <Link href={`/brands/${brandId}/narrative-angles/${angle.id}`}>
                        <div className="cursor-pointer">
                          <div className="text-sm font-semibold leading-snug group-hover:text-primary transition-colors" data-testid={`text-angle-name-${angle.id}`}>
                            {angle.name}
                          </div>
                          {angle.summary && (
                            <p className="text-[11.5px] text-muted-foreground/70 leading-snug line-clamp-2 mt-0.5">{angle.summary}</p>
                          )}
                        </div>
                      </Link>
                    </td>
                    <td className="px-4 py-3 align-top hidden md:table-cell">
                      <span className="text-[11.5px] font-medium text-foreground/80 whitespace-nowrap">{persona?.name || "Persona deleted"}</span>
                    </td>
                    <td className="px-4 py-3 align-top hidden md:table-cell">
                      <span className="text-[11.5px] font-medium text-foreground/80 whitespace-nowrap">{itemName}</span>
                    </td>
                    <td className="px-2 py-3 text-center text-xs tabular-nums hidden lg:table-cell">{angle.resonanceScore ?? "—"}</td>
                    <td className="px-2 py-3 text-center text-xs tabular-nums hidden lg:table-cell">{angle.proofScore ?? "—"}</td>
                    <td className="px-2 py-3 text-center text-xs tabular-nums hidden lg:table-cell">{angle.distinctivenessScore ?? "—"}</td>
                    <td className="px-2 py-3 text-center text-xs tabular-nums hidden lg:table-cell">{angle.emotionalScore ?? "—"}</td>
                    <td className="px-3 py-3 text-center">
                      {avg > 0 ? (
                        <span className="text-sm font-bold tabular-nums text-primary">{avg.toFixed(1)}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => favMutation.mutate(angle.id)}
                        className="p-1.5 rounded-full hover:bg-muted/50 transition-colors"
                        data-testid={`button-fav-${angle.id}`}
                        title="Toggle favorite"
                      >
                        <Heart className={`h-3.5 w-3.5 ${angle.isFavorite ? "fill-red-500 text-red-500" : "text-muted-foreground/40"}`} />
                      </button>
                      <button
                        onClick={() => deleteMutation.mutate(angle.id)}
                        className="p-1.5 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        data-testid={`button-delete-angle-${angle.id}`}
                        title="Delete angle"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground/40 hover:text-red-500" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PastGenerationsTable({
  generations,
  personaMap,
  productMap,
  offeringMap,
  brandId,
  onDelete,
  deletePending,
}: {
  generations: NarrativeGeneration[];
  personaMap: Map<string, Persona>;
  productMap: Map<string, Product>;
  offeringMap: Map<string, Offer>;
  brandId: string;
  onDelete: (id: string) => void;
  deletePending: boolean;
}) {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<"created-desc" | "created-asc" | "persona" | "product" | "status" | "opportunities">("created-desc");

  const genItemName = (g: NarrativeGeneration) =>
    resolvePromotedName(g.promotedItemType, g.productId, g.offeringId, productMap, offeringMap);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? generations.filter((g) => {
        const personaName = (personaMap.get(g.personaId || "")?.name || "Persona deleted").toLowerCase();
        const itemName = genItemName(g).toLowerCase();
        return personaName.includes(q) || itemName.includes(q) || g.status.toLowerCase().includes(q);
      })
    : generations;

  const sorted = [...filtered].sort((a, b) => {
    switch (sortBy) {
      case "created-asc":
        return new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
      case "persona": {
        const pa = personaMap.get(a.personaId || "")?.name || "Persona deleted";
        const pb = personaMap.get(b.personaId || "")?.name || "Persona deleted";
        return pa.localeCompare(pb);
      }
      case "product": {
        return genItemName(a).localeCompare(genItemName(b));
      }
      case "status":
        return a.status.localeCompare(b.status);
      case "opportunities":
        return (b.angleCount || 0) - (a.angleCount || 0);
      case "created-desc":
      default:
        return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
    }
  });

  return (
    <div>
      <TableToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search persona, product, or status…"
        sortBy={sortBy}
        onSortChange={(v) => setSortBy(v as typeof sortBy)}
        sortOptions={[
          { value: "created-desc", label: "Newest first" },
          { value: "created-asc", label: "Oldest first" },
          { value: "persona", label: "Persona (A → Z)" },
          { value: "product", label: "Promoted (A → Z)" },
          { value: "status", label: "Status" },
          { value: "opportunities", label: "Opportunities (high → low)" },
        ]}
        resultCount={sorted.length}
        totalCount={generations.length}
        testIdPrefix="generations"
      />
      <div className="rounded-xl border border-border/60 overflow-hidden bg-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40 border-b border-border/60 text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="text-left font-semibold px-4 py-2.5">Persona</th>
                <th className="text-left font-semibold px-4 py-2.5">Promoted</th>
                <th className="text-left font-semibold px-4 py-2.5">Status</th>
                <th className="text-center font-semibold px-3 py-2.5 hidden sm:table-cell">Opportunities</th>
                <th className="text-left font-semibold px-4 py-2.5 hidden sm:table-cell">Created</th>
                <th className="px-3 py-2.5 w-[1%]"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((gen) => {
              const persona = personaMap.get(gen.personaId || "");
              const itemName = genItemName(gen);
              return (
                <tr
                  key={gen.id}
                  className="border-b border-border/40 last:border-b-0 hover:bg-muted/20 transition-colors group"
                  data-testid={`row-generation-${gen.id}`}
                >
                  <td className="px-4 py-3">
                    <Link href={`/brands/${brandId}/narrative-generations/${gen.id}`}>
                      <span className="text-sm font-semibold cursor-pointer group-hover:text-primary transition-colors">
                        {persona?.name || "Persona deleted"}
                      </span>
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <Link href={`/brands/${brandId}/narrative-generations/${gen.id}`}>
                      <span className="text-sm font-semibold cursor-pointer group-hover:text-primary transition-colors">
                        {itemName}
                      </span>
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <GenerationStatusBadge status={gen.status} />
                  </td>
                  <td className="px-3 py-3 text-center hidden sm:table-cell">
                    {gen.angleCount && gen.angleCount > 0 ? (
                      <span className="text-xs tabular-nums text-foreground/80">{gen.angleCount}</span>
                    ) : (
                      <span className="text-xs text-muted-foreground/50">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span className="text-[11.5px] text-muted-foreground whitespace-nowrap">
                      {gen.createdAt
                        ? new Date(gen.createdAt).toLocaleDateString("en-US", {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                          })
                        : "—"}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground/40 hover:text-destructive hover:bg-destructive/10 rounded-full"
                      onClick={() => onDelete(gen.id)}
                      disabled={deletePending}
                      data-testid={`button-delete-generation-${gen.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                    <Link href={`/brands/${brandId}/narrative-generations/${gen.id}`}>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground/40 hover:text-primary rounded-full"
                        data-testid={`button-open-generation-${gen.id}`}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </Link>
                  </td>
                </tr>
              );
            })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function TableToolbar({
  search,
  onSearchChange,
  searchPlaceholder,
  sortBy,
  onSortChange,
  sortOptions,
  resultCount,
  totalCount,
  testIdPrefix,
}: {
  search: string;
  onSearchChange: (v: string) => void;
  searchPlaceholder: string;
  sortBy: string;
  onSortChange: (v: string) => void;
  sortOptions: { value: string; label: string }[];
  resultCount: number;
  totalCount: number;
  testIdPrefix: string;
}) {
  return (
    <div className="flex flex-col sm:flex-row gap-2 mb-3">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/60 pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          className="pl-9 h-9 rounded-full text-xs"
          data-testid={`input-search-${testIdPrefix}`}
        />
      </div>
      <div className="flex items-center gap-2">
        <Select value={sortBy} onValueChange={onSortChange}>
          <SelectTrigger className="h-9 rounded-full text-xs w-auto min-w-[180px] gap-1.5" data-testid={`select-sort-${testIdPrefix}`}>
            <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground/70" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {sortOptions.map((o) => (
              <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-[11px] text-muted-foreground tabular-nums whitespace-nowrap" data-testid={`text-count-${testIdPrefix}`}>
          {search.trim() ? `${resultCount} of ${totalCount}` : `${totalCount}`}
        </span>
      </div>
    </div>
  );
}
